import { LucideCheck } from "lucide-react"
import { useEffect, useRef } from "react"
import { cn } from "~/utils"

export default function ControlPopoverContent<T>({
  className,
  title,
  isOpen,
  onClose,
  onClick,
  options,
}: {
  className?: string
  title: string
  isOpen: boolean
  onClose: () => void
  onClick: (value: T) => void
  options: {
    label: string
    value: T
    selected: boolean
  }[]
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const target = selectedRef.current ?? listRef.current?.querySelector<HTMLButtonElement>("button")
    target?.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, onClose])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []
    )
    const idx = buttons.indexOf(document.activeElement as HTMLButtonElement)

    if (e.key === "ArrowDown") {
      e.preventDefault()
      buttons[(idx + 1) % buttons.length]?.focus()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      buttons[(idx - 1 + buttons.length) % buttons.length]?.focus()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={listRef}
      role="menu"
      aria-label={title}
      onKeyDown={handleKeyDown}
      className={cn(
        "absolute bottom-full right-0 z-50 mb-2 flex w-36 flex-col overflow-hidden rounded-lg border bg-white shadow-md sm:w-48 sm:rounded-2xl",
        className
      )}
    >
      <div className="hidden border-b px-4 py-2 sm:block">
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="flex flex-col overflow-auto p-1 sm:p-2">
        {options.map(({ label, selected, value }) => (
          <button
            key={label}
            ref={selected ? selectedRef : undefined}
            role="menuitemradio"
            aria-checked={selected}
            onClick={() => onClick(value)}
            className={cn(
              "flex w-full items-center justify-between rounded px-2 py-1 text-left text-[10px] sm:text-sm",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5300EC]",
              selected ? "bg-[#EEE5FD] font-bold" : "hover:bg-gray-100"
            )}
          >
            {label}
            {selected && <LucideCheck className="h-3 w-3 text-[#5300EC] sm:h-4 sm:w-4" />}
          </button>
        ))}
      </div>
    </div>
  )
}
